import { ClickAwayListener, Grid } from "@mui/material";
import Link from "next/link";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Color from "../../../../assets/colors";
import { getLastActiveAtTime } from "../../../../helpers/conditional_rendering";
import { currencies } from "../../../../helpers/constant";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import {
  CompanyNameRow,
  CompanyVerifiedBadge,
} from "../../AccordionJobCard/styles";
import SingleJobApplyButtonGroup from "../SingleJobApplyButtonGroup/SingleJobApplyButtonGroup";
import {
  CardContentStyledWithLogo,
  CardStyledWithLogo,
  CompanyLogoImgStyled,
  CurrencyIconStyled,
  DivStyled,
  IconTextContainer,
  InfoCirleFilledStyled,
  JobTitle,
  LabelStyled,
  LinkStyled,
  MapPinStyled,
  StatusBadgeContainer,
  StatusClockIcon,
  StyledTooltip,
  StyledTooltipText,
  TooltipInfoText,
} from "./styles";

function SingleJobHighlightWithLogo(props) {
  const { job, locationFilter, handleHref } = props;
  const { t } = useTranslation();

  const { company } = job || {};

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const getJobTypeLabel = (jobTypeValue) => {
    if (!jobTypeValue) return "";
    const key = jobTypeValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    console.log("jobTypeValue", jobTypeValue, "key", key);
    return t(`companydetail.job_type_labels.${key}`, {
      defaultValue: jobTypeValue,
    });
  };

  const getTime = (job) => {
    let createdAtDate = job.createdAt || job.created_at;
    let jobCreatedAt = getLastActiveAtTime(createdAtDate, t);

    return jobCreatedAt;
  };

  const getStatusBadge = (employerStatus) => {
    switch (employerStatus) {
      case "Expiring Soon":
        return {
          text: "Expiring soon",
          color: "#F75443",
          icon: <StatusClockIcon />,
        };
      case "Actively Reviewing":
        return {
          text: "Actively reviewing",
          color: "#6AA84F",
          icon: null,
        };
      case "Be an Early Applicant":
        return {
          text: "Be an early applicant",
          color: "#512ACC",
          icon: null,
        };
      default:
        return null;
    }
  };

  const statusBadge = getStatusBadge(job?.employerStatus);

  const renderstatusBadge = () =>
    statusBadge ? (
      <StatusBadgeContainer $color={statusBadge.color}>
        {statusBadge.icon}
        {statusBadge.text}
      </StatusBadgeContainer>
    ) : null;

  function formatSalary(input, crossBorderData) {
    const geoEnv = process.env.NEXT_PUBLIC_JSW_GEOLOCATION;
    let currency;

    // prioritise source_country as it may be jobs utilising "cross border token"
    // if no source_country, it means the job is from the target geolocation of ur api (e.g. my-api OR sg-api)
    // therefore, fallback is to just use geolocation ENV
    if (crossBorderData?.source_country) {
      currency = currencies[crossBorderData?.source_country];
    } else if (geoEnv === "sg") {
      currency = "SGD";
    } else {
      currency = "RM";
    }

    if (!input?.includes(" - ")) {
      const salaryNumber = Number(input?.replace(currency, ""));
      if (salaryNumber < 10000) {
        return `${currency} ${salaryNumber}`;
      }
      const salaryInK = salaryNumber / 1000;
      const startDecimals = salaryInK?.toFixed(1) % 1 === 0 ? 0 : 1;
      return `${currency} ${salaryInK?.toFixed(startDecimals)}K`;
    }
    const [startSalary, endSalary] = input?.split(" - ");

    const startNumber = Number(startSalary?.replace("RM", ""));

    if (startNumber === 0) {
      return `${endSalary}`;
    }

    const endNumber = Number(endSalary?.replace(currency, ""));

    if (startNumber < 10000 && endNumber < 10000) {
      return `${currency} ${startNumber} - ${currency} ${endNumber}`;
    }

    const startSalaryInK = startNumber / 1000;
    const endSalaryInK = endNumber / 1000;

    const startDecimals = startSalaryInK?.toFixed(1) % 1 === 0 ? 0 : 1;
    const endDecimals = endSalaryInK?.toFixed(1) % 1 === 0 ? 0 : 1;

    return `${currency} ${startSalaryInK?.toFixed(
      startDecimals
    )}K - ${currency} ${endSalaryInK?.toFixed(endDecimals)}K`;
  }

  function TooltipChip() {
    return (
      <ClickAwayListener
        onClickAway={() => {
          setTooltipOpen(false);
        }}
      >
        <StyledTooltip
          open={tooltipOpen}
          onClick={() => {
            setTooltipOpen(true);
          }}
          onMouseEnter={() => {
            setTooltipOpen(true);
          }}
          onClose={() => {
            setTooltipOpen(false);
          }}
          componentsProps={{
            tooltip: {
              sx: {
                display: "flex",
                width: "234px",
                padding: "10px 14px",
                flexDirection: "column",
                alignItems: "center",
                borderRadius: "12px",
                border: "2px solid #222222",
                background: "#FFF",
              },
            },
          }}
          sx={{
            display: "flex !important",
            gap: "4px",
          }}
          title={
            <TooltipInfoText>
              {props.job?.company?.name} is a{" "}
              <span style={{ fontWeight: 700 }}>headhunting firm</span>, the job
              openings listed here are posted on behalf of our clients
            </TooltipInfoText>
          }
          placement="bottom-start"
        >
          <StyledTooltipText>RECRUITMENT firm</StyledTooltipText>
          <InfoCirleFilledStyled />
        </StyledTooltip>
      </ClickAwayListener>
    );
  }

  const isCompanyVerified =
    company?.verified === true ||
    company?.name === "Hiredly" ||
    company?.name === "Hiredly X";

  return (
    <CardStyledWithLogo>
      <CardContentStyledWithLogo>
        {job?.category !== "aggregated" ? (
          <DivStyled
            container
            flexWrap={"nowrap"}
            alignItems={"flex-start"}
            columnGap={"15px"}
            marginBottom={"15px"}
          >
            {company?.logo ? (
              <CompanyLogoImgStyled
                src={company?.logo}
                alt="company-logo-image"
              />
            ) : null}
            <DivStyled>
              <JobTitle variant="h1">{job?.title}</JobTitle>
              <Grid
                display={"flex"}
                gap={"8px"}
                alignItems={"flex-start"}
                sx={{ minWidth: 0 }}
              >
                <CompanyNameRow
                  sx={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <LinkStyled
                    href={`/companies/${company?.slug}`}
                    aria-disabled={props.job?.company?.hide ? true : false}
                    onClick={() => {
                      sendTrackingEvent({
                        event: "CE_click-company-job-card-ijp",
                        "company-id": company?.id,
                        "job-id": job?.id,
                      });
                    }}
                    sx={{
                      minWidth: 0,
                      maxWidth: "100%",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "wrap",
                      overflow: "hidden",
                    }}
                  >
                    {company?.name}
                  </LinkStyled>
                  {isCompanyVerified && (
                    <CompanyVerifiedBadge aria-label="Verified company" />
                  )}
                </CompanyNameRow>
                {company?.recruitmentFirm && <TooltipChip />}
              </Grid>
            </DivStyled>
          </DivStyled>
        ) : null}

        <IconTextContainer
          aria-label="job-salary"
          gap={"35px"}
          marginBottom={"19px"}
        >
          <CurrencyIconStyled />
          <LabelStyled aria-label="job-salary-value" variant="body1">
            {job?.salary === "Undisclosed" || job?.salary === "Undefined"
              ? "Undisclosed"
              : formatSalary(job?.salary, job?.crossBorderData)}
          </LabelStyled>
        </IconTextContainer>

        <IconTextContainer
          aria-label="job-location"
          gap={"35px"}
          marginBottom={"19px"}
        >
          <MapPinStyled />
          <LabelStyled
            aria-label="job-location-value"
            variant="body1"
            flexWrap={"wrap"}
            textOverflow={"ellipsis"}
            overflow={"hidden"}
            whiteSpace={"nowrap"}
          >
            {locationFilter(job)}
          </LabelStyled>
        </IconTextContainer>

        <IconTextContainer marginBottom={"25px"}>
          <DivStyled
            container
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            {job.jobType || job.job_type ? (
              <Link
                href={handleHref("jobType", job)}
                style={{ textDecoration: "none", color: "currentcolor" }}
              >
                <DivStyled
                  style={{
                    background: Color.lightBlue,
                    padding: "0.25rem 0.5rem",
                    borderRadius: "0.25rem",
                    color: Color.blue,
                    cursor: "pointer",
                  }}
                >
                  <LabelStyled
                    variant="body1"
                    fontWeight={"bold"}
                    fontSize={"14px"}
                    aria-label="job-type"
                    sx={{
                      color: Color.blue,
                    }}
                  >
                    {job?.jobType
                      ? getJobTypeLabel(job?.jobType)
                      : job?.job_type
                      ? getJobTypeLabel(job?.job_type)
                      : null}
                  </LabelStyled>
                </DivStyled>
              </Link>
            ) : null}
            {statusBadge && renderstatusBadge()}
          </DivStyled>
        </IconTextContainer>

        <SingleJobApplyButtonGroup job={job} jobHighlight={true} />
      </CardContentStyledWithLogo>
    </CardStyledWithLogo>
  );
}

export default SingleJobHighlightWithLogo;
